import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/form/checkbox';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { clearErrors, setPayment } from '@/reducers/ots';
import { AppDispatch } from '@/root/store';
import { FormatCurrency } from '@/routes/root';
import { ShowEvent } from '@/services/events';
import {
  GetOtsPaymentChannels,
  GetOtsTransactionsByEvent,
  PayTicket,
  ShowBalance,
} from '@/services/ots';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, CreditCardIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

export interface PayModalBase extends React.HTMLAttributes<HTMLDivElement> {
  payModal: boolean;
  setPayModal?: any;
}

export const defaultPaymentMethod = {
  name: '',
  code: '',
  settings: {
    type: '',
    amount: 0,
  },
};

export const OtsPayModal: React.FC<PayModalBase> = ({
  payModal = false,
  setPayModal,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [ticketQuantity, setTicketQuantity] = useState(0);
  const [adminfee, setAdminFee] = useState(0);
  const [ticketTotal, setTicketTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [paymentFee, setPaymentFee] = useState(0);
  const [detailPayment, setDetailPayment] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState({
    id: null,
    name: null,
    price: 0,
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState(defaultPaymentMethod);
  const { slug } = useParams();
  const dispatch: AppDispatch = useDispatch();

  const { channels, payment, errors } = useSelector((state: any) => state.ots);
  const { event, tickets } = useSelector((state: any) => state.event);

  const setData = (value: any, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  const pay = () => {
    dispatch(
      PayTicket(event.id, {
        ...formData,
        ticket_id: selectedTicket.id,
        quantity: ticketQuantity,
        payment_method: selectedPaymentMethod.code,
      })
    );
  };

  useEffect(() => {
    dispatch(ShowEvent(slug));
    dispatch(GetOtsPaymentChannels(slug));
  }, [slug]);

  useEffect(() => {
    if (payment.redirect_uri && payModal) {
      window?.snap.pay(payment.redirect_uri, {
        onSuccess: (result: any) => {
          toast.success('Pembayaran berhasil diterima');

          dispatch(GetOtsTransactionsByEvent(slug, ''));
          setFormData({});
          setPayModal(false);
        },
        onPending: (result: any) => {
          toast.error('Pembayaran belum diterima');
        },
        onError: (result: any) => {
          toast.error('Pembayaran gagal');
        },
        onClose: () => {
          toast.error('Mohon selesaikan pembayaran dalam 15 menit');
        },
      });
    } else if (
      payment.code === 'cash' &&
      selectedPaymentMethod.code === 'cash'
    ) {
      toast.success('Pembayaran berhasil diterima');

      dispatch(setPayment({}));
      dispatch(ShowBalance(slug));
      dispatch(GetOtsTransactionsByEvent(slug, ''));
      setPayModal(false);
    }
  }, [payment]);

  useEffect(() => {
    let fees = 0;
    switch (event.settings?.fees?.assigned_type) {
      case 'full-guest':
        fees = event?.settings?.fees?.amount;
        break;
      case 'full-org':
        fees = 0;
        break;
      case 'fifty-fifty':
        fees = event?.settings?.fees?.amount / 2;
        break;
      default:
        fees = 5000;
    }

    let subtotal = selectedTicket?.price * ticketQuantity;
    let admin_fee = fees * ticketQuantity;
    let settings = selectedPaymentMethod.settings;
    let total = subtotal + admin_fee;

    let amount =
      settings?.type === 'nominal'
        ? settings?.amount
        : settings?.amount * subtotal;

    setAdminFee(admin_fee);
    setPaymentFee(amount);
    setTicketTotal(subtotal);
    setTotal(total + amount);
  }, [selectedPaymentMethod, selectedTicket, ticketQuantity]);

  return (
    <Modal
      show={payModal}
      onRequestClose={() => setPayModal(false)}
      title='Beli tiket'
      body={
        <div className='flex flex-col gap-4'>
          {errors?.checkout && (
            <Alert color='danger'>
              <p className='h-fit flex-grow text-sm'>{errors.checkout}</p>
            </Alert>
          )}
          {event?.customer_data
            ?.filter((item: any) => item.checked)
            .map((field: any, index: string) => (
              <FormLabel
                key={index}
                label={field.label}
                errorText={
                  ['name', 'phone', 'email'].includes(field.name)
                    ? errors[field.name]
                    : errors[`additional_owner_data.${field.name}`]
                }
                required
              >
                {field?.media_url && (
                  <img
                    className={'h-fit w-full object-fill'}
                    src={field?.media_url}
                    alt={field.name}
                  />
                )}

                {!['select', 'checkbox'].includes(field.type) && (
                  <Input
                    type={
                      ['name', 'phone', 'email'].includes(field.name)
                        ? field.name
                        : field.type
                    }
                    name={field.name}
                    leftDeco={
                      field.name === 'phone'
                        ? event.organizer?.country?.phone_code
                        : ''
                    }
                    onChange={(e: any) => setData(e.target.value, field.name)}
                    required
                  />
                )}

                {field.type === 'select' && (
                  <Select onValueChange={(e: any) => setData(e, field.name)}>
                    <SelectTrigger>
                      <SelectValue defaultValue='others' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {field?.possible_values?.map(
                          (value: string, index: string) => (
                            <SelectItem key={index} value={value}>
                              {value}
                            </SelectItem>
                          )
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}

                {field.type === 'checkbox' && (
                  <div className={'flex flex-grow gap-2'}>
                    <Checkbox checked={false} />
                    <span>{field.label}</span>
                  </div>
                )}
              </FormLabel>
            ))}
          <FormLabel label='Pilih tiket' errorText={errors.ticket_id} required>
            <Select
              onValueChange={(value) => {
                let ticket = tickets?.filter(
                  (item: any) => item.id === value
                )[0];
                setSelectedTicket({
                  id: ticket.id,
                  price: ticket.price,
                  name: ticket.name,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue defaultValue='others' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {tickets
                    ?.filter((ticket: any) => ticket.saleable === 'ots')
                    .map((ticket: any, index: number) => (
                      <SelectItem key={index} value={ticket.id}>
                        {ticket.name}
                      </SelectItem>
                    ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormLabel>
          {selectedTicket.id && (
            <FormLabel label={'Atur jumlah tiket'} errorText={errors.quantity}>
              <div className='flex items-center gap-3'>
                <div className='flex items-start gap-1 self-start rounded-md border-[1px] border-neutral-40 p-2 text-sm'>
                  <Button
                    onClick={() => {
                      if (ticketQuantity !== 0) {
                        setTicketQuantity(ticketQuantity - 1);
                      }
                    }}
                  >
                    -
                  </Button>
                  <span className='my-auto w-[31px] text-center'>
                    {ticketQuantity}
                  </span>
                  <Button
                    onClick={() => {
                      setTicketQuantity(ticketQuantity + 1);
                    }}
                  >
                    +
                  </Button>
                </div>
              </div>
            </FormLabel>
          )}
          <FormLabel
            label='Pilih metode pembayaran'
            errorText={errors.payment_method}
            required
          >
            <Select
              onValueChange={(value) => {
                let channel = channels?.filter(
                  (item: any) => item.code === value
                )[0];
                setSelectedPaymentMethod({
                  code: channel.code,
                  name: channel.name,
                  settings: channel.settings,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue defaultValue='other_qris' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {channels?.map((channel: any, index: string) => (
                    <SelectItem key={index} value={channel.code}>
                      {channel.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormLabel>
        </div>
      }
      footer={
        <div>
          <div className='flex justify-between gap-3'>
            <div className={'my-auto'}>
              <div
                className={'flex cursor-pointer'}
                onClick={() => {
                  setDetailPayment(!detailPayment);
                }}
              >
                <div className={'my-auto'}>
                  <span className='text-md font-semibold text-red-500'>
                    <FormatCurrency amount={total} />
                  </span>
                </div>
                <div className={'my-auto'}>
                  <ChevronDownIcon className={'ml-1 h-[16px] text-red-500'} />
                </div>
              </div>
            </div>
            <div className='flex justify-end gap-3'>
              <Button
                variant='neutral'
                onClick={() => setPayModal(false)}
                leftIcon={<NoSymbolIcon />}
              >
                Batal
              </Button>
              <Button
                onClick={() => {
                  pay();
                }}
                leftIcon={<CreditCardIcon />}
              >
                Bayar
              </Button>
            </div>
          </div>
          {detailPayment && (
            <div className={'mt-4 text-left'}>
              <div className={'mb-1'}>
                <span className='text-900 text-md font-semibold'>
                  Subtotal: <FormatCurrency amount={ticketTotal} />
                </span>
              </div>
              <div className={'mb-1'}>
                <span className='text-900 text-md font-semibold'>
                  Biaya Admin: <FormatCurrency amount={adminfee} />
                </span>
              </div>
              <div className={'mb-1'}>
                <span className='text-900 text-md font-semibold'>
                  Biaya Transaksi: <FormatCurrency amount={paymentFee} />
                </span>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};

OtsPayModal.displayName = 'OtsSetupModal';
